import { ArrowRight, Braces, Check, CloudCog, Gauge, Layers3, ShieldCheck, Sparkles, Trophy, Upload } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button, Card } from '../components/ui'

export function LandingPage() {
  const auth = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const competition = searchParams.get('competition')
  const region = searchParams.get('region')
  const hasTarget = !!(competition && region)
  const authFrom = (location.state as { authFrom?: string } | null)?.authFrom
  const prompted = useRef(false)

  useEffect(() => {
    if (hasTarget && !auth.loading && !auth.user && !prompted.current) {
      prompted.current = true
      auth.openAuth('wechat')
    }
  }, [hasTarget, auth.loading, auth.user, auth])

  if (hasTarget) {
    if (auth.loading) return <div className="page-loader">正在进入赛事投稿…</div>
    if (auth.user) return <Navigate to={{ pathname: '/submit/competition', search: `?${new URLSearchParams({ competition: competition!, region: region! }).toString()}` }} replace />
  } else if (authFrom && auth.user && authFrom !== location.pathname + location.search) {
    return <Navigate to={authFrom} replace />
  }

  const enter = (path: string) => auth.user ? undefined : (event: React.MouseEvent) => { event.preventDefault(); auth.openAuth('wechat') }
  return <div className="landing">
    <section className="hero">
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-copy">
        <span className="eyebrow"><i/> MIXER AI DEVELOPER PLATFORM · 2.1</span>
        <h1>让每一个 Agent<br/><em>拥有标准化工具接口</em></h1>
        <p>从代码识别、MCP 转换、在线托管到自动评测，在一个平台完成 Agent 工具的完整交付。</p>
        <div className="hero-actions">
          <Link className="button button-primary" to="/submit/regular" onClick={enter('/submit/regular')}><Upload size={17}/>常规上传 Agent</Link>
          <Link className="button competition-button" to="/submit/competition" onClick={enter('/submit/competition')}><Trophy size={17}/><b>赛事专属</b>进入赛事中心</Link>
        </div>
        <div className="proof"><span><Check/>无需改造现有 API</span><span><Check/>HTTP / SSE / stdio</span><span><Check/>多租户隔离</span></div>
      </div>
      <WorkbenchPreview />
    </section>

    <section className="section" aria-labelledby="entry-title">
      <SectionHeading kicker="START HERE" title="选择你的发布路径" description="日常作品与赛事投稿完全分离，字段与流程各自清晰。" />
      <div className="path-grid">
        <Link to="/submit/regular" onClick={enter('/submit/regular')} className="path-card regular"><span>01 · STANDARD</span><Upload/><h3>常规上传 Agent</h3><p>无需选择赛事或填写参赛资料，完善 Agent 信息即可进入个人作品空间。</p><b>开始上传 <ArrowRight/></b></Link>
        <Link to="/submit/competition" onClick={enter('/submit/competition')} className="path-card competition"><span>02 · EVENT ONLY</span><Trophy/><h3>赛事投稿中心</h3><p>手动选择当前开放的比赛与赛区，补充团队和联系人信息后进入评测流程。</p><b>进入赛事入口 <ArrowRight/></b></Link>
      </div>
    </section>

    <section className="section" id="features">
      <SectionHeading kicker="PLATFORM" title="一套完整的 Agent 工具基础设施" description="把协议、部署、评测和隔离交给平台，专注 Agent 本身。" />
      <div className="feature-grid">
        <Card className="feature-card feature-large"><Braces/><span>CODE TO MCP</span><h3>从现有代码生成 MCP Server</h3><p>自动识别 HTTP 接口和参数结构，生成可运行、可下载、可托管的标准 MCP 服务。</p><code>POST /api/search → MCP search_knowledge()</code></Card>
        <Card className="feature-card"><Gauge/><span>EVALUATION</span><h3>自动评测与质量门禁</h3><p>Exact、Schema、LLM Judge 和性能阈值统一评分。</p><strong className="score">92<small>/100</small></strong></Card>
        <Card className="feature-card"><CloudCog/><span>RUNTIME</span><h3>在线托管，多协议接入</h3><p>兼容 Streamable HTTP、SSE 与 stdio。</p></Card>
        <Card className="feature-card"><ShieldCheck/><span>MULTI-TENANCY</span><h3>租户隔离与可观测性</h3><p>账号资源、运行状态和服务地址清晰可见。</p></Card>
      </div>
    </section>

    <section className="evaluation-banner">
      <div><span className="eyebrow">AUTOMATED EVALUATION</span><h2>不只“能运行”，还要证明它足够可靠</h2><p>连接任意 MCP Server，自动发现工具、组织用例、执行评分并沉淀趋势报告。</p><Link className="button button-primary" to="/evaluations">打开评测工作台 <ArrowRight size={16}/></Link></div>
      <div className="score-panel"><div><span>Evaluation Run #0248</span><b>Completed</b></div><strong>92.4</strong>{['功能正确性 96','Schema 质量 90','性能稳定性 91'].map(x=><p key={x}><i/><span>{x}</span></p>)}</div>
    </section>

    <section className="final-cta"><Sparkles/><span className="eyebrow">BUILD · SHIP · EVALUATE</span><h2>把你的 Agent，连接到更大的工具生态</h2><p>从第一次上传到稳定运行，每一步都有清晰、标准化的能力支撑。</p><Button onClick={() => auth.user ? window.location.assign('/submit/regular') : auth.openAuth('wechat')}>免费开始使用 <ArrowRight size={16}/></Button></section>
  </div>
}

function SectionHeading({ kicker, title, description }: { kicker: string; title: string; description: string }) {
  return <div className="section-heading"><div><span className="eyebrow">{kicker}</span><h2>{title}</h2></div><p>{description}</p></div>
}

function WorkbenchPreview() {
  return <div className="workbench"><div className="workbench-bar"><i/><i/><i/><span>app.mixer.ai / workspace</span><b>● LIVE</b></div><div className="workbench-body"><aside><span>M</span><i/><i/><i/></aside><div className="workbench-content"><small>MY AGENTS</small><div className="workbench-title"><strong>知识检索助手</strong><b>Running</b></div><div className="pipeline"><span>01<br/><b>代码解析</b></span><i/><span>02<br/><b>6 个工具</b></span><i/><span>03<br/><b>在线运行</b></span></div><div className="preview-cards"><div><small>调用成功率</small><strong>99.8%</strong></div><div><small>Streamable HTTP</small><code>/mcp/a8f2...9ce1</code><b>已连接</b></div></div><div className="preview-log">14:32:08　 tools/call　 search_knowledge　 <b>200 · 184ms</b></div></div></div></div>
}
